import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import ShopLayout from "../layouts/ShopLayout.vue";
import AuthLayout from "../layouts/AuthLayout.vue";
import AdminLayout from "../layouts/AdminLayout.vue";
import SubPageLayout from "../layouts/SubPageLayout.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      component: ShopLayout,
      children: [
        {
          path: "",
          name: "home",
          component: () => import("../views/user/HomeView.vue"),
        },
        {
          path: "about",
          name: "about",
          component: () => import("../views/user/AboutUsView.vue"),
        },
        {
          path: "login",
          name: "login",
          component: () => import("../views/user/LoginView.vue"),
        },
        {
          path: "signup",
          name: "signup",
          component: () => import("../views/user/SignupView.vue"),
        },
        {
          path: "shops",
          name: "shops",
          component: () => import("../views/user/ShopListView.vue"),
        },
        {
          path: "shops/:id",
          name: "shop-detail",
          component: () => import("../views/user/ShopDetailView.vue"),
        },
      ],
    },
    {
      path: "/articles",
      component: SubPageLayout,
      children: [
        {
          path: "news",
          name: "news",
          component: () => import("../views/user/NewsListView.vue"),
        },
        {
          path: "user-profile",
          name: "user-profile",
          component: () => import("../views/profile/UserProfileView.vue"),
        },
        {
          path: "news/:id",
          name: "news-detail",
          component: () => import("../views/user/NewsDetailView.vue"),
        },
        {
          path: "articles",
          name: "articles",
          component: () => import("../views/user/ArticleListView.vue"),
        },
        {
          path: "articles/:id",
          name: "article-detail",
          component: () => import("../views/user/ArticleDetailView.vue"),
        },
        {
          path: "eat-at",
          name: "eat-at",
          component: () => import("../views/user/EatAtListView.vue"),
        },
        {
          path: "eat-at/:id",
          name: "eat-at-detail",
          component: () => import("../views/user/EatAtDetailView.vue"),
        },
      ],
    },
    {
      path: "/admin",
      component: AdminLayout,
      meta: { requiresAuth: true, roles: ["admin", "moderator", "super_admin"] },
      children: [
        {
          path: "",
          name: "admin-dashboard",
          component: () => import("../views/admin/DashboardView.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "users",
          name: "admin-users",
          component: () =>
            import("../views/admin/userManagement/UserManagement.vue"),
          meta: { roles: ["super_admin"] },
        },
        {
          path: "profile",
          name: "admin-profile",
          component: () => import("../views/profile/AdminProfileView.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "news",
          name: "admin-news",
          component: () => import("../views/admin/news/NewsManagement.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "news/create",
          name: "admin-news-create",
          component: () => import("../views/admin/news/NewsForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "news/edit/:id",
          name: "admin-news-edit",
          component: () => import("../views/admin/news/NewsForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "articles",
          name: "admin-articles",
          component: () =>
            import("../views/admin/articles/ArticleManagement.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "articles/create",
          name: "admin-article-create",
          component: () => import("../views/admin/articles/ArticleForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "articles/edit/:id",
          name: "admin-article-edit",
          component: () => import("../views/admin/articles/ArticleForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "shops",
          name: "admin-shops",
          component: () => import("../views/admin/shops/ShopManagement.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "shops/create",
          name: "admin-shop-create",
          component: () => import("../views/admin/shops/ShopForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "shops/edit/:id",
          name: "admin-shop-edit",
          component: () => import("../views/admin/shops/ShopForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "hero",
          name: "admin-hero",
          component: () => import("../views/admin/hero/HeroManagement.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "hero/create",
          name: "admin-hero-create",
          component: () => import("../views/admin/hero/HeroForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
        {
          path: "hero/edit/:id",
          name: "admin-hero-edit",
          component: () => import("../views/admin/hero/HeroForm.vue"),
          meta: { roles: ["admin", "moderator", "super_admin"] },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("../views/errors/NotFoundView.vue"),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    }
    return { top: 0 };
  },
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const routeWithRoles = [...to.matched]
    .reverse()
    .find((record) => record.meta.roles);
  const requiredRoles = routeWithRoles?.meta.roles || [];

  // Wait for auth to be ready
  if (!authStore.authReady) {
    await authStore.init();
  }

  // Handle authentication
  if (requiresAuth && !authStore.user) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  // Force-refresh claims on protected routes so stale tokens cannot keep old rights
  if (requiredRoles.length > 0 && authStore.user) {
    try {
      await authStore.refreshClaims();
    } catch (error) {
      console.error("Failed to refresh claims before navigation:", error);
      next({ name: "login", query: { redirect: to.fullPath } });
      return;
    }
  }

  // Role checks use Custom Claims only
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.includes(authStore.userRole);

    if (!hasRequiredRole) {
      next({ name: "not-found" });
      return;
    }
  }

  next();
});

export default router;
